import i18n from '@/language/i18n';
import dateFormatter from '@/services/formatter_services/date-formatter';

export const DEFAULT_FORMAT = 'YYYY-MM-DD';
export const DEFAULT_FORMAT_WITH_TIME = 'YYYY-MM-DD HH:mm:ss';
export const DEFAULT_FORMAT_WITH_TIME_FOR_FILE_NAME = 'YYYY-MM-DD HH_mm_ss';
export const MONTH_YEAR_FORMAT = 'MM.YYYY';

export const DEFAULT_CONFIG = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit'
}

export function getToday() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export const TODAY = getToday();

export const CURRENT_YEAR = new Date()
  .getFullYear();

export default {
  getFirstDayOfMonth(year, month) {
    const firstDayOfMonth = new Date(year, month, 1);
    return dateFormatter.formatDate(firstDayOfMonth);
  },

  getFirstDayOfCurrentMonth() {
    const currentDate = new Date();

    const firstDayOfCurrentMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );
    return dateFormatter.formatDate(firstDayOfCurrentMonth);
  },

  getFirstDayOfLastMonth() {
    const today = new Date();
    const lastMonth = new Date(today.setMonth(today.getMonth() - 1));

    const firstDayOfLastMonth = new Date(
      lastMonth.getFullYear(),
      lastMonth.getMonth(),
      1
    );
    return dateFormatter.formatDate(firstDayOfLastMonth);
  },

  getLastDayOfMonth(year, month) {
    const lastDayOfMonth = new Date(year, month + 1, 0);
    return dateFormatter.formatDate(lastDayOfMonth);
  },

  getLastDayOfCurrentMonth() {
    const currentDate = new Date();

    const lastDayOfCurrentMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0
    );
    return dateFormatter.formatDate(lastDayOfCurrentMonth);
  },

  getLastMonth() {
    let today = (new Date());
    return new Date(today.setMonth(today.getMonth() - 1));
  },

  /* Use only for unformatted DTO date types */
  isDateInRange(date, from, until) {
    if (!date || !from || !until) return false;
    let dateSplit = date.split("-");
    let fromSplit = from.split("-");
    let untilSplit = until.split("-");

    var fromDate = new Date(fromSplit[0], fromSplit[1] - 1, fromSplit[2]);
    var untilDate = new Date(untilSplit[0], untilSplit[1] - 1, untilSplit[2]);
    var dateSubject = new Date(dateSplit[0], dateSplit[1] - 1, dateSplit[2]);

    return dateSubject >= fromDate && dateSubject <= untilDate;
  },

  /* Use only for unformatted DTO date types
     Considers open ranges, e.g: null - (2023-03-07)
  */
  isDateInOpenRange(date, from, until) {
    if (!date) return false;
    if (!from && !until) return true;

    let dateSplit = date.split("-");
    const dateSubject = new Date(dateSplit[0], dateSplit[1] - 1, dateSplit[2]);
    let fromDate = undefined;
    let untilDate = undefined;

    if (from) {
      const fromSplit = from.split("-");
      fromDate = new Date(fromSplit[0], fromSplit[1] - 1, fromSplit[2]);
    }

    if (until) {
      const untilSplit = until.split("-");
      untilDate = new Date(untilSplit[0], untilSplit[1] - 1, untilSplit[2]);
    }

    if (from && until)
      return dateSubject >= fromDate && dateSubject <= untilDate;

    if (!until)
      return dateSubject >= fromDate

    return dateSubject <= untilDate;
  },

  isSameDay(dateA, dateB) {
    return (
      dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate()
    );
  },

  getCurrentDateString() {
    const today = new Date();

    const hours = String(today.getHours()).padStart(2, "0");
    const minutes = String(today.getMinutes()).padStart(2, "0");
    const seconds = String(today.getSeconds()).padStart(2, "0");

    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear();

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  },

  getToday,

  parseRange(rangeString, useMonthParsing, dateTimeMode, includeMonth) {
    const splitNewVal = rangeString.split('-');
    let startVal = splitNewVal.length > 0 ? this.parseDate(splitNewVal[0], useMonthParsing, dateTimeMode, includeMonth) : undefined;
    let endVal = splitNewVal.length == 2 ? this.parseDate(splitNewVal[1], useMonthParsing, dateTimeMode, includeMonth) : undefined;
    if (startVal == undefined) return undefined;
    return endVal == undefined || endVal == startVal ? [startVal] : [startVal, endVal];
  },

  parseDate(dateString, useMonthParsing, dateTimeMode, includeMonth) {
    //By first formatting the input with a library function, we essentially reuse its date parsing functionality.
    //Whatever defined value this method outputs is a valid date.
    const formattedDateString = this.formatISOString(
      this.parseToISO(dateString, useMonthParsing, dateTimeMode, includeMonth),
      useMonthParsing,
      dateTimeMode
    );

    if (formattedDateString == undefined) return undefined;

    const isoDate = this.parseToISO(formattedDateString, useMonthParsing, dateTimeMode, includeMonth);

    return (
      dateTimeMode
        ? isoDate.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/)
        : isoDate.match(/^\d{4}-\d{2}-\d{2}$/)
    )
      ? isoDate
      : undefined;
  },

  parseToISO(dateString, useMonthParsing, dateTimeMode, includeMonth) {
    let minutes;
    let hours;
    let year;
    let month;
    let day;
    const cleanDate = dateString.replace(/\D/g, '');
    const today = new Date();

    if (useMonthParsing) {
      day = this.getDayOfMonthPicker(cleanDate, includeMonth);
      month = cleanDate.substring(0, 2);
      year = cleanDate.substring(2);
    } else {
      day =
        i18n.locale === 'en'
          ? cleanDate.substring(2, 4)
          : cleanDate.substring(0, 2);
      month =
        i18n.locale === 'en'
          ? cleanDate.substring(0, 2)
          : cleanDate.substring(2, 4);
      year =
        cleanDate.substring(4).length <= 4
          ? cleanDate.substring(4)
          : cleanDate.substring(4, 8);

      if (dateTimeMode) {
        hours =
          cleanDate.substring(4).length <= 4
            ? '00'
            : cleanDate.substring(8, 10);
        minutes =
          cleanDate.substring(4).length <= 4
            ? '00'
            : cleanDate.substring(10, 12);
        if (hours.length == 0) hours = today.getHours();
        if (minutes.length == 0) minutes = today.getMinutes();
      }
    }

    if (day.length == 0) day = today.getDate();
    if (month.length == 0) month = today.getMonth() + 1; //returned month ranges from 0 to 11
    if (year.length == 0) year = today.getFullYear();
    if (year.length == 2) year = '20' + year;

    return (
      `${year}-${month}-${day}` +
      (dateTimeMode ? ` ${hours}:${minutes}` : ``)
    );
  },

  formatISOString(dateString, useMonthParsing, dateTimeMode) {
    //month ranges from 0 to 11
    let dateObj;
    let month;
    let parts;
    if (dateString == undefined) return undefined;

    if (!dateString)
      return;

    if (dateTimeMode) {
      let dateStringSplit = dateString.split(' ');
      parts = dateStringSplit[0].split('-');
      const timeParts =
        dateStringSplit.length > 1
          ? dateStringSplit[1]?.split(':')
          : [0, 0];
      month = parts[1].slice(0) - 1;

      dateObj = new Date(
        parts[0],
        month,
        parts[2],
        timeParts[0],
        timeParts[1],
        0,
        0
      );

      return (
        dateObj.toLocaleDateString(i18n.locale, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }) +
        ' ' +
        `${timeParts[0]}`.padStart(2, '0') +
        ':' +
        `${timeParts[1]}`.padStart(2, '0')
      );
    } else {
      parts = dateString.split('-');
      month = parts[1].slice(0) - 1; //month ranges from 0 to 11

      // Trim off time
      if (parts.length > 2) {
        parts[2] = parts[2].substring(0, 2);
      }

      if (useMonthParsing) {
        dateObj = new Date(parts[0], month, 1, 0, 0, 0, 0);
        return dateObj.toLocaleDateString(i18n.locale, {
          month: '2-digit',
          year: 'numeric',
        });
      } else {
        dateObj = new Date(parts[0], month, parts[2], 0, 0, 0, 0);
        return dateObj.toLocaleDateString(i18n.locale, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      }
    }
  },

  formatISOStringWithoutYear(dateString, useMonthParsing, dateTimeMode) {
    const formatted = this.formatISOString(dateString, useMonthParsing, dateTimeMode);

    if (!formatted)
      return formatted;

    const withoutYear = formatted
      .replace(/[\/\.\-]\d{4}/, '')
      .replace(/\d{4}[\/\.\-]/, '')
      .trim();

    return withoutYear.endsWith('.') ? withoutYear : `${withoutYear}.`;
  },

  getNextMonthOfDate(dateString) {
    const [year, month] = dateString.split('-').map(Number);
    const nextMonth = new Date(year, month - 1, 1);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    const nextYear = nextMonth.getFullYear();
    const nextMonthNumber = nextMonth.getMonth() + 1;

    return `${nextYear}-${String(nextMonthNumber).padStart(2, '0')}`;
  },

  getLastMonthOfDate(dateString) {
    const [year, month] = dateString.split('-').map(Number);
    const lastMonth = new Date(year, month - 1, 1);
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    const lastYear = lastMonth.getFullYear();
    const lastMonthNumber = lastMonth.getMonth() + 1;

    return `${lastYear}-${String(lastMonthNumber).padStart(2, '0')}`;
  },

  getDayOfMonthPicker(val, includeMonth) {
    if (!includeMonth) return '01';
    const splitVal = val.split('-');
    const day = new Date(splitVal[0], splitVal[1], 0).getDate();
    return day
  },

  extractDateParts(dateString) {
    if (!dateString || typeof dateString !== 'string')
      return undefined;

    // Match pattern: 4 digits (year), optional dash/separator, optional 1-2 digits (month)
    const dateMatch = dateString.match(/(\d{4})[-\/\s]*(\d{1,2})?/);

    if (!dateMatch)
      return undefined;

    const year = parseInt(dateMatch[1], 10);
    const month = parseInt(dateMatch[2], 10);

    return { year, month };
  }
}
